import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useIsFetchingNewPeriod } from '@features/expertReports'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import type { APIError } from '../../../utils'
import { fetchExpertBalanceSheet, type FetchExpertBalanceSheetParams } from '../query-api'
import type { ExpertBalanceSheetReport } from '../types/expertBalanceSheetReport'

interface UseFetchExpertBalanceSheetResult {
  isError: boolean
  isFetchingNewPeriod: boolean
  isLoading: boolean
  report: ExpertBalanceSheetReport | undefined
}

export const useFetchExpertBalanceSheet = ({
  compareCount,
  compareWith,
  organizationId,
  period,
}: FetchExpertBalanceSheetParams): UseFetchExpertBalanceSheetResult => {
  const queryProps = useMemo<FetchExpertBalanceSheetParams>(
    () => ({ compareCount, compareWith, organizationId, period }),
    [compareCount, compareWith, organizationId, period],
  )

  const {
    data: reportData,
    isError,
    isIdle,
    isLoading,
    isPreviousData,
  } = useQuery<ExpertBalanceSheetReport, APIError>(
    [QueryKeys.ExpertBalanceSheet, queryProps],
    () => fetchExpertBalanceSheet(queryProps),
    { enabled: !!organizationId && !!period, keepPreviousData: true, staleTime: defaultStaleTime },
  )

  const isFetchingNewPeriod = useIsFetchingNewPeriod(period, isPreviousData)

  return {
    isError,
    isFetchingNewPeriod: isFetchingNewPeriod && !isError,
    isLoading: isIdle || isLoading,
    report: isError && isFetchingNewPeriod ? undefined : reportData,
  }
}
